import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from './Header.js';
import { Sidebar } from './Sidebar.js';
import { Footer } from './Footer.js';
import { CommandBar } from '@/components/command-bar/CommandBar.js';
import { useCommandBar } from '@/hooks/useCommandBar.js';
import { useFavoriteStore } from '@/stores/useFavoriteStore.js';
import { useAppStore } from '@/stores/useAppStore.js';

const DRAWER_WIDTH = 200;

export const AppLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presentationMode = useAppStore((s) => s.presentationMode);
  const setPresentationMode = useAppStore((s) => s.setPresentationMode);
  const favoriteItems = useFavoriteStore((s) => s.items);
  const favorites = useMemo(
    () => Object.values(favoriteItems).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [favoriteItems]
  );

  // Prompt 14 Phase 3/4: Presentation Mode (?presentation=true)
  // 명시적으로 presentation=true로 진입하면 활성화되고, 메뉴 이동 시에도 유지된다.
  // presentation=false로 명시할 때만 해제된다.
  useEffect(() => {
    const param = searchParams.get('presentation');
    if (param === 'true' && !presentationMode) {
      setPresentationMode(true);
    } else if (param === 'false' && presentationMode) {
      setPresentationMode(false);
    }
  }, [searchParams, presentationMode, setPresentationMode]);

  const commandBar = useCommandBar({
    favorites: favorites.map((f) => ({ id: f.id, assetType: f.assetType, name: f.name, href: f.href })),
    onDemoStart: () => navigate('/dashboard'),
    onSelfTest: () => navigate('/settings?tab=diagnostics'),
    onNewSimulationTest: () => navigate('/scenario?new=simulation'),
    onNewTestSuite: () => navigate('/scenario?new=suite'),
  });

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };
  const handleDrawerClose = () => setMobileOpen(false);

  const isOpen = isDesktop ? desktopOpen : mobileOpen;

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Header onMenuToggle={handleDrawerToggle} desktopOpen={isDesktop ? desktopOpen : undefined} />
      <Sidebar open={isOpen} onClose={handleDrawerClose} isDesktop={isDesktop} width={DRAWER_WIDTH} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          p: 0,
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
        {!presentationMode && <Footer />}
      </Box>
      <CommandBar
        open={commandBar.open}
        onClose={() => commandBar.setOpen(false)}
        query={commandBar.query}
        onQueryChange={commandBar.setQuery}
        items={commandBar.items}
        selectedIndex={commandBar.selectedIndex}
        onSelect={commandBar.setSelectedIndex}
        onExecute={commandBar.execute}
        inputRef={commandBar.inputRef}
      />
    </Box>
  );
};
